import { useCallback, useMemo, useState } from 'react'

import { CallQueueRow } from '@/components/calls/call-queue-row'
import { getPageHeader } from '@/constants'
import { CallsListSkeleton } from '@/components/layout/calls-list-row-skeleton'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { PageHeaderStack } from '@/components/navigation/page-header-stack'
import {
  PageHeaderActions,
  PageHeaderActionSecondary,
} from '@/components/navigation/page-header-actions'
import { Activity, RefreshCw } from 'lucide-react'
import { useListFilterBar } from '@/hooks/use-list-filter-bar'
import {
  useCallRecords,
  useLiveCallRecordsStream,
  type CallRecordListItem,
} from '@/hooks/use-call-records'
import {
  CALL_EVALUATIONS_FILTER_DEFINITIONS,
  type CallEvaluationsFilterState,
  type CallEvaluationsTab,
} from '@/lib/list-filters/call-evaluations'

const LIST_LIMIT = 200

function matchesTab(row: CallRecordListItem, tab: CallEvaluationsTab): boolean {
  if (tab === 'all') return true
  if (tab === 'in_progress') return row.evaluation_status === 'evaluating'
  if (tab === 'completed')
    return row.evaluation_status === 'completed'
  // attention: completed calls that still need a finished evaluation
  if (tab === 'attention') {
    return (
      row.is_complete &&
      row.evaluation_status !== 'completed' &&
      row.evaluation_status !== 'evaluation_failed'
    )
  }
  return false
}

export function CallEvaluationsPage() {
  const { title, description } = getPageHeader(
    '/calls/evaluations',
    'Evaluations queue',
    'Completed calls that need evaluation work, plus in-progress and done.'
  )

  const [search, setSearch] = useState('')
  const [tab, setTab] = useState<CallEvaluationsTab>('attention')
  const { data, isLoading, error, refetch } = useCallRecords(LIST_LIMIT)
  useLiveCallRecordsStream()

  const counts = useMemo(() => {
    const rows = data ?? []
    let attention = 0
    let in_progress = 0
    let completed = 0
    for (const row of rows) {
      if (matchesTab(row, 'attention')) attention += 1
      if (matchesTab(row, 'in_progress')) in_progress += 1
      if (matchesTab(row, 'completed')) completed += 1
    }
    return {
      attention,
      in_progress,
      completed,
      all: rows.length,
    }
  }, [data])

  const filterCtx = useMemo(() => ({ counts }), [counts])

  const evaluationFilters: CallEvaluationsFilterState = useMemo(
    () => ({ search, status: tab }),
    [search, tab],
  )

  const onFiltersChange = useCallback((next: CallEvaluationsFilterState) => {
    setTab(next.status)
  }, [])

  const clearFilters = useCallback(() => {
    setSearch('')
    setTab('attention')
  }, [])

  const { filterBar } = useListFilterBar({
    listId: 'callEvaluations',
    definitions: CALL_EVALUATIONS_FILTER_DEFINITIONS,
    filters: evaluationFilters,
    onFiltersChange,
    ctx: filterCtx,
    onClearAll: clearFilters,
    search: {
      searchId: 'call-evaluations-search',
      searchPlaceholder: 'Search by caller or call ID…',
      searchValue: search,
      onSearchChange: setSearch,
    },
  })

  function renderQueue(rows: CallRecordListItem[]) {
    if (isLoading) {
      return <CallsListSkeleton count={4} ariaLabel="Loading evaluations" />
    }
    if (error) {
      return (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      )
    }
    if (rows.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">
          No calls in this queue for the latest {LIST_LIMIT} records.
        </p>
      )
    }
    return rows.map((row) => (
      <CallQueueRow
        key={row.id}
        variant="evaluation"
        row={row}
        showEvalSummary
      />
    ))
  }

  const visibleRows = useMemo(() => {
    const trimmed = search.trim().toLowerCase()
    const allRows = data ?? []
    return allRows.filter((row) => {
      if (!matchesTab(row, tab)) return false
      if (!trimmed) return true
      const haystack = [row.vapi_call_id, row.customer_name, row.customer_number]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(trimmed)
    })
  }, [data, search, tab])

  return (
    <PageContainer>
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading />}
          title={title}
          description={description}
          icon={<Activity className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              secondary={
                <PageHeaderActionSecondary onClick={() => refetch()}>
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </PageHeaderActionSecondary>
              }
            />
          }
        />

        {filterBar}
      </PageHeaderStack>

      <p className="text-sm text-muted-foreground">
        Evaluations are stored on each call record (summary and status). Open a row for
        the full transcript and evaluation text.
      </p>

      <div className="space-y-2">{renderQueue(visibleRows)}</div>
    </PageContainer>
  )
}
